import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import ItemLocation from "./ItemLocation";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import { deepFreeze, formatPO, timestampToDate } from "../util/misc";
import {
  cloneDeep,
  every,
  forEach,
  isArray,
  isBoolean,
  isEmpty,
  sortBy,
} from "lodash";
import { diff } from "deep-object-diff";

export const PACKING_SLIP_FIELDS = {
  orderPO: "",
  ackID: "",
  vendor: "",
  receivedDate: null,
  document: null,
  missingDocument: false,
  reason: "",
  items: {},
  screens: [], //{ included: 0, locations: [], description: "" },
  hardware: [], //{ included: 0, locations: [], description: "" },
  report: null,
  total: null,
  directShip: false,
  locked: false,
  itemLocations: [],

  // Skip QC / Cross-dock
  skipQC: false,
  crossDockVehicle: null,
  skipQCReason: "",

  // Barcodes scanned during receiving (populated by bulk receiving)
  scannedBarcodes: [],

  // The vendor truck this slip's material came off, recorded from the packages
  // scanned against it. A slip is one shipped truck: across two Marvin orders,
  // 8 trucks shipped / 8 slips and 7 shipped / 7 slips, with the one PLANNED
  // truck in each having no slip because it had not left the plant.
  //
  // Trucks are shared between orders -- the same truck number appears on
  // several -- so the identifying pair is (order, truck), which is exactly what
  // a slip under an order already is.
  //
  // Recorded rather than inferred: dates line up closely enough to prove the
  // relationship but not to join on (one order had two trucks arriving the same
  // day), and an order that breaks the pattern should surface as a conflict
  // rather than silently mis-scope.
  truckNumber: "",
  stopID: "",

  // Status
  receivedComplete: null,
  receivedIssues: false,
  qcInspected: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(PACKING_SLIP_FIELDS);

export const PACKING_SLIP_STATUS_TYPES = {
  receivedComplete: {
    order: 1,
    label: "packingSlip.receivedComplete",
    icon: "done",
    color: "positive",
  },
  receivedIncomplete: {
    order: 2,
    label: "packingSlip.receivedIncomplete",
    icon: "backorder",
    color: "warning",
  },
  receivedIssues: {
    order: 3,
    label: "packingSlip.receivedIssues",
    icon: "issue",
    color: "negative",
  },
  qc: {
    order: 1,
    label: "Quality Control",
    icon: "quality_control",
    color: "warning",
  },
  qcComplete: {
    order: 2,
    label: "packingSlip.qcComplete",
    icon: "done",
    color: "positive",
  },
};
deepFreeze(PACKING_SLIP_STATUS_TYPES);

export default class PackingSlip extends DataStructure {
  constructor(projectID, saleID, orderID, id, data = {}) {
    // Backward compatibility
    if (!data.orderPO) {
      data.orderPO = projectID + saleID;
    }
    if ("warehouseLocation" in data && !("itemLocations" in data)) {
      data.itemLocations = [
        {
          id: data.warehouseLocation,
          bin: null,
          type: "warehouse",
          itemType: "unit",
          quantity: data.total.ready,
        },
      ];
      delete data.warehouseLocation;
    }
    if (data.screens && !isArray(data.screens)) {
      if (data.screens.included) {
        data.screens = [data.screens];
      } else {
        delete data.screens;
      }
    }
    if (data.hardware && !isArray(data.hardware)) {
      if (data.hardware.included) {
        data.hardware = [data.hardware];
      } else {
        delete data.hardware;
      }
    }

    data = sanitize(data, PACKING_SLIP_FIELDS);

    // Convert to Date. NB: PackingSlip has no `date` field (it uses
    // receivedDate) — converting a non-existent `date` re-added `date:
    // undefined` after sanitize stripped it, and toFirestore() then wrote that
    // undefined, throwing "Unsupported field value: undefined (field date)" on
    // create. Only convert the real date fields.
    data.receivedDate = timestampToDate(data.receivedDate);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.saleID = saleID || null;
    this.orderID = orderID || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
    });

    // Documents
    if (this.data.document) {
      this.data.document = this.document.data;
    }
    if (this.data.report) {
      this.data.report = this.report.data;
    }
  }

  // Permissions

  canBeEditedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      (!this.id || user.isAdmin || !this.locked) &&
      user.isEmployee
    );
  }

  canBeDeletedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      !this.locked &&
      // QC-inspected packing slips can't simply be deleted: SAVE_PACKING_SLIP
      // wrote the QC report directly onto every sale-item (`quantityReady`,
      // direct-ship `quantityDelivered/Scheduled`, `itemLocations`) and
      // bumped sale-level counters (`status.delivery.ready`, `total.screens`,
      // etc.). DELETE_PACKING_SLIP doesn't reverse any of that, so deleting
      // a QC'd slip would leave inflated quantities pinned with no source.
      !this.data.qcInspected &&
      user.isEmployee &&
      (user.id === this.data.createdBy || user.isAdmin)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return PackingSlip.getPath(
      this.projectID,
      this.saleID,
      this.orderID,
      this.id
    );
  }

  static getPath(projectID, saleID, orderID, id) {
    let path = `projects/${projectID}/sales/${saleID}/orders/${orderID}/packing-slips`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(packingSlip) {
    return packingSlip.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new PackingSlip(
      snapshot.ref.parent.parent.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  get isFinalPackingSlip() {
    const sale = this.sale;
    // Same missing-entry gap as Ack.isFinalAck: `status.acks` entries are
    // dropped by DELETE_ORDER and absent on legacy sales, so the lookup can
    // come back undefined. Read it optionally rather than throwing mid-render.
    return this.id && sale
      ? sale.status.acks?.[this.data.ackID]?.fullyReceived ?? null
      : null;
  }

  get rules() {
    return {
      ackID: [(a) => Boolean(a && a.trim()) || "required"],
      document: [
        () =>
          Boolean(
            this.data.missingDocument ||
              (this.data.document && this.document.isValid)
          ) || "validDocumentRequired",
      ],
      // Truck receiving keeps screens and hardware on the slip's own rows, so
      // a truck that carried only those has no pooled location; their rows
      // say where it went (and the screens/hardware rules check them).
      itemLocations: [
        (w) =>
          this.data.directShip ||
          this.data.skipQC ||
          ((w || []).every((l) => new ItemLocation(l).isValid) &&
            ((w && w.length > 0) || this.hasScreenOrHardwareLocations)) ||
          "itemLocationsRequired",
      ],
      skipQCReason: [(r) => !this.data.skipQC || Boolean(r && r.trim())],
      crossDockVehicle: [
        (v) =>
          !this.data.skipQC ||
          this.data.crossDockVehicle ||
          !this.data.crossDockVehicle,
      ],
      hardware: [
        (h) =>
          !h.length ||
          h.every(
            (h) =>
              Boolean(h.description.trim()) &&
              h.locations.every((l) => new ItemLocation(l).isValid) &&
              h.included ===
                h.locations.reduce((total, l) => total + (l.quantity || 0), 0)
          ) ||
          "hardwareInvalid",
      ],
      screens: [
        (s) =>
          !s.length ||
          s.every(
            (s) =>
              Boolean(s.description.trim()) &&
              s.locations.every((l) => new ItemLocation(l).isValid) &&
              s.included ===
                s.locations.reduce((total, l) => total + (l.quantity || 0), 0)
          ) ||
          "screensInvalid",
      ],
      receivedDate: [(a) => Boolean(a) || "required"],
      receivedComplete: [(a) => typeof a === "boolean" || "required"],
      receivedIssues: [(a) => typeof a === "boolean" || "required"],
      reason: [
        (r) =>
          !this.data.missingDocument ||
          Boolean(r && r.trim()) ||
          "reasonRequiredMissingDocument",
      ],
    };
  }

  get isValid() {
    const data = PackingSlip.sanitize(cloneDeep(this.data));
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(data[key]) === true)
    );
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(
        diff(
          {
            ...this.data,
            ackID: "",
            vendor: "",
            receivedDate: null,
            itemLocations: this.data.itemLocations.filter(
              (l) => !ItemLocation.isEmpty(l)
            ),
          },
          PACKING_SLIP_FIELDS
        )
      )
    );
  }

  isEqual(packingSlip) {
    return (
      this.id === packingSlip.id &&
      isEmpty(
        diff(
          PackingSlip.sanitize(cloneDeep(this.data)),
          PackingSlip.sanitize(cloneDeep(packingSlip.data))
        )
      )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.document) {
      const doc = new DocumentRef(data.document);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.document = null;
      }
    }
    if (data.report) {
      const doc = new DocumentRef(data.report);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.report = null;
      }
    }
    return data;
  }

  static sanitize(data, removeUnsaved) {
    if (data.missingDocument) {
      data.document = null;
    } else {
      data.reason = "";
    }
    data.itemLocations = data.itemLocations.filter((l) => l.quantity > 0);
    forEach(data.items, (item) => {
      item.locations = item.locations.filter((l) => l.quantity > 0);
    });
    data.hardware.forEach((item) => {
      item.description = item.description.trim();
      item.locations = item.locations.filter((l) => l.quantity > 0);
    });
    data.screens.forEach((item) => {
      item.description = item.description.trim();
      item.locations = item.locations.filter((l) => l.quantity > 0);
    });
    PackingSlip.cleanDocuments(data, removeUnsaved);
    return data;
  }

  // Formatting

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }

  get sale() {
    return this.saleID
      ? store.state.main.sales[this.projectID + this.saleID]
      : null;
  }

  get po() {
    return formatPO(this.projectID + this.saleID, this.data.orderPO);
  }

  get order() {
    return this.orderID ? store.state.main.orders[this.orderID] : null;
  }

  get ack() {
    return this.ackID ? store.state.main.acks[this.ackID] : null;
  }

  get document() {
    return this.data.document ? new DocumentRef(this.data.document) : null;
  }

  get report() {
    return this.data.report ? new DocumentRef(this.data.report) : null;
  }

  get items() {
    let items = [];
    forEach(this.data.items, (status, id) => {
      if (status.included) {
        items.push(store.state.main.saleItems[id]);
      }
    });
    return sortBy(items, ["line", "accessoryIndex"]);
  }

  get issues() {
    return sortBy(
      Object.values(store.state.main.issues).filter(
        (issue) => issue.data.packingSlipID === this.id
      ),
      "psLine"
    );
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get receivedDate() {
    return timestampToDate(this.data.receivedDate) || null;
  }

  get receivedStatus() {
    if (this.data.receivedComplete) {
      return "receivedComplete";
    } else {
      return "receivedIncomplete";
    }
  }

  get status() {
    if (this.data.qcInspected) {
      return "qcComplete";
    } else {
      return "qc";
    }
  }

  get itemLocations() {
    return this.data.itemLocations
      .concat(
        ...this.data.hardware.map((h) => h.locations),
        ...this.data.screens.map((s) => s.locations)
      )
      .map((l) => new ItemLocation(l))
      .filter((l) => l.isValid);
  }

  get hasScreenOrHardwareLocations() {
    return [...(this.data.screens || []), ...(this.data.hardware || [])].some(
      (row) => (row.locations || []).some((l) => l.quantity > 0)
    );
  }

  get receivedStatusType() {
    const type = PACKING_SLIP_STATUS_TYPES[this.receivedStatus];
    return type ? { ...type, ...COLORS[type.color] } : {};
  }

  get statusType() {
    const type = PACKING_SLIP_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : {};
  }
}
